<template>
  <div class="sci-view-container">
    <!-- Top Glassmorphic Navigation -->
    <header class="sci-header">
      <div class="sci-header-left">
        <button class="sci-btn-back" @click="home">
          <i class="fas fa-arrow-left"></i>
          <span>{{ $t('gestionale') || 'Gestionale' }}</span>
        </button>
        <div class="sci-title-group">
          <h1 class="sci-title">
            <i class="fas fa-receipt me-2 text-primary"></i>
            {{ $t('riepilogo') || 'Riepilogo Giornaliero Ordini & Incassi' }}
          </h1>
          <p class="sci-subtitle">Rendicontazione dettagliata per piatti venduti, comande al tavolo e ordini asporto</p>
        </div>
      </div>
      <div class="sci-header-right">
        <button 
          class="sci-btn sci-btn-secondary" 
          @click="exportToExcel" 
          :disabled="viewMode === 'piatti' ? !items.length : !unifiedOrders.length"
        >
          <i class="fas fa-file-excel text-success me-1"></i>
          <span>Esporta Excel</span>
        </button>
        <button class="sci-btn sci-btn-primary" @click="fetchData" :disabled="loading">
          <i class="fas fa-sync-alt me-1" :class="{ 'fa-spin': loading }"></i>
          <span>Aggiorna</span>
        </button>
      </div>
    </header>

    <!-- Main Content -->
    <main class="sci-main-content">
      <!-- 3 KPI Cards Banner -->
      <section class="kpi-bento-grid mb-4">
        <div class="sci-card kpi-card">
          <div class="kpi-icon-box gross-icon">
            <i class="fas fa-cash-register"></i>
          </div>
          <div class="kpi-info">
            <span class="kpi-label">Totale Lordo</span>
            <span class="kpi-number font-monospace">€ {{ summaryStats.gross.toFixed(2) }}</span>
            <span class="kpi-subtext text-muted">Valore complessivo vendite</span>
          </div>
        </div>

        <div class="sci-card kpi-card">
          <div class="kpi-icon-box delivery-fee-icon">
            <i class="fas fa-percentage"></i>
          </div>
          <div class="kpi-info">
            <span class="kpi-label">Quote Piattaforme Delivery</span>
            <span class="kpi-number font-monospace text-danger">- € {{ summaryStats.deliveryFees.toFixed(2) }}</span>
            <span class="kpi-subtext text-muted">Commissioni JustEat / Glovo / Deliveroo</span>
          </div>
        </div>

        <div class="sci-card kpi-card kpi-net-card">
          <div class="kpi-icon-box net-icon">
            <i class="fas fa-coins"></i>
          </div>
          <div class="kpi-info">
            <span class="kpi-label text-success">Totale Netto Cassa</span>
            <span class="kpi-number font-monospace text-success">€ {{ summaryStats.net.toFixed(2) }}</span>
            <span class="kpi-subtext text-muted">Incasso effettivo al netto di commissioni</span>
          </div>
        </div>
      </section>

      <!-- Filter & Controls Toolbar -->
      <section class="sci-card mb-4 toolbar-card">
        <div class="toolbar-grid">
          <!-- Calendar Selector -->
          <div class="date-picker-section">
            <label class="control-label">
              <i class="fas fa-calendar-day me-1 text-primary"></i> Data di Riferimento:
            </label>
            <v-date-picker 
              v-model="date" 
              mode="date" 
              class="sci-date-input"
            />
          </div>

          <!-- Filter By Channel -->
          <div class="filter-pills-section">
            <label class="control-label">Filtra Canale:</label>
            <div class="segmented-control">
              <button 
                type="button" 
                class="segment-btn" 
                :class="{ active: filter === 'tutti' }" 
                @click="filter = 'tutti'"
              >
                Tutti
              </button>
              <button 
                type="button" 
                class="segment-btn" 
                :class="{ active: filter === 'tavolo' }" 
                @click="filter = 'tavolo'"
              >
                <i class="fas fa-chair me-1"></i> Tavoli
              </button>
              <button 
                type="button" 
                class="segment-btn" 
                :class="{ active: filter === 'asporto' }" 
                @click="filter = 'asporto'"
              >
                <i class="fas fa-motorcycle me-1"></i> Asporto / Delivery
              </button>
            </div>
          </div>

          <!-- Group By Mode -->
          <div class="group-pills-section">
            <label class="control-label">Raggruppamento:</label>
            <div class="segmented-control">
              <button 
                type="button" 
                class="segment-btn" 
                :class="{ active: viewMode === 'piatti' }" 
                @click="viewMode = 'piatti'"
              >
                <i class="fas fa-utensils me-1"></i> Per Piatti
              </button>
              <button 
                type="button" 
                class="segment-btn" 
                :class="{ active: viewMode === 'ordini' }" 
                @click="viewMode = 'ordini'"
              >
                <i class="fas fa-receipt me-1"></i> Singoli Ordini
              </button>
            </div>
          </div>
        </div>
      </section>

      <!-- Data Table Card -->
      <section class="sci-card table-card">
        <!-- View: Piatti -->
        <div class="table-responsive" v-if="viewMode === 'piatti'">
          <table class="sci-table">
            <thead>
              <tr>
                <th>Nome Piatto</th>
                <th style="width: 200px;">Portata / Categoria</th>
                <th style="width: 150px; text-align: center;">Quantità Venduta</th>
                <th style="width: 180px; text-align: right;">Ricavo Totale (€)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in items" :key="item.piatto" class="sci-table-row">
                <td>
                  <span class="item-name">{{ item.piatto }}</span>
                </td>
                <td>
                  <span class="badge-portata">{{ item.portata || 'Standard' }}</span>
                </td>
                <td style="text-align: center;">
                  <span class="badge-qty font-monospace">{{ item.quantita }}</span>
                </td>
                <td style="text-align: right;">
                  <span class="item-revenue font-monospace">
                    € {{ item.guadagno ? item.guadagno.toFixed(2) : '0.00' }}
                  </span>
                </td>
              </tr>
              <tr v-if="!items.length && !loading">
                <td colspan="4" class="sci-empty-state">
                  <div class="empty-content text-center py-5">
                    <div class="empty-icon-halo">
                      <i class="fas fa-utensils"></i>
                    </div>
                    <h3 class="empty-title">Nessun piatto venduto</h3>
                    <p class="empty-desc">Nessuna consumazione registrata per la data e i filtri selezionati.</p>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- View: Ordini -->
        <div class="table-responsive" v-else>
          <table class="sci-table">
            <thead>
              <tr>
                <th style="width: 240px;">Ordine & Tipo</th>
                <th>Articoli Ordinati</th>
                <th style="width: 140px; text-align: right;">Lordo (€)</th>
                <th style="width: 160px; text-align: right;">Comm. Delivery (€)</th>
                <th style="width: 140px; text-align: right;">Netto (€)</th>
                <th style="width: 160px; text-align: center;">Metodo Pagamento</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="order in unifiedOrders" :key="order.id" class="sci-table-row">
                <td>
                  <div class="order-id-group">
                    <span 
                      class="order-type-badge" 
                      :class="order.type === 'tavolo' ? 'type-table' : 'type-asporto'"
                    >
                      <i :class="order.type === 'tavolo' ? 'fas fa-chair me-1' : 'fas fa-motorcycle me-1'"></i>
                      {{ order.type === 'tavolo' ? 'Tavolo' : 'Asporto' }}
                    </span>
                    <span class="order-delivery-badge" v-if="order.delivery">
                      {{ order.delivery }}
                    </span>
                    <span class="order-identifier font-monospace">{{ order.identifier }}</span>
                    <small v-if="order.cameriere" class="text-muted d-block mt-1">
                      <i class="fas fa-user-tie me-1"></i> {{ order.cameriere }}
                    </small>
                  </div>
                </td>
                <td>
                  <div class="order-items-list">
                    <div v-for="(item, idx) in order.items" :key="idx" class="order-subitem">
                      <span class="subitem-qty font-monospace">{{ item.quantita }}x</span>
                      <span class="subitem-name">{{ item.piatto }}</span>
                      <span v-if="item.aggiunta && item.aggiunta.length" class="text-success small ms-1">
                        (+{{ item.aggiunta.map(a => a.descrizione || a).join(', ') }})
                      </span>
                      <span v-if="item.senza && item.senza.length" class="text-danger small ms-1">
                        (-{{ item.senza.join(', ') }})
                      </span>
                      <span v-if="item.nota" class="text-muted small ms-1 fst-italic">
                        [{{ item.nota }}]
                      </span>
                    </div>
                  </div>
                </td>
                <td style="text-align: right;">
                  <span class="font-monospace">€ {{ order.totale.toFixed(2) }}</span>
                </td>
                <td style="text-align: right;">
                  <span v-if="order.deliveryFee > 0" class="text-danger font-monospace">
                    - € {{ order.deliveryFee.toFixed(2) }}
                  </span>
                  <span v-else class="text-muted">-</span>
                </td>
                <td style="text-align: right;">
                  <span class="order-net-val font-monospace">
                    € {{ order.netto.toFixed(2) }}
                  </span>
                </td>
                <td style="text-align: center;">
                  <select 
                    v-model="order.digitale" 
                    @change="updatePaymentMethod(order.id, order.type, order.digitale)"
                    class="sci-select-sm"
                  >
                    <option :value="false">💵 Contanti</option>
                    <option :value="true">💳 Digitale</option>
                  </select>
                </td>
              </tr>
              <tr v-if="!unifiedOrders.length && !loading">
                <td colspan="6" class="sci-empty-state">
                  <div class="empty-content text-center py-5">
                    <div class="empty-icon-halo">
                      <i class="fas fa-receipt"></i>
                    </div>
                    <h3 class="empty-title">Nessun ordine registrato</h3>
                    <p class="empty-desc">Nessun ordine trovato per la data e i parametri selezionati.</p>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import { store } from '../store/store.js'
import moment from 'moment'
import * as XLSX from 'xlsx'

export default {
  name: 'RiepilogoOrdini',
  data() {
    return {
      store,
      date: new Date(),
      filter: 'tutti',
      viewMode: 'piatti',
      items: [],
      ordiniTavolo: [],
      ordiniAsporto: [],
      deliveryPlatforms: [],
      loading: false
    }
  },
  computed: {
    unifiedOrders() {
      const orders = [];
      
      this.ordiniTavolo.forEach(order => {
        const gross = Math.max(0, (order.totale || 0) - (order.sconto || 0));
        const delFee = this.calculateDeliveryFee(order.delivery, gross);
        orders.push({
          id: order._id,
          type: 'tavolo',
          identifier: `Tavolo ${order.numero}`,
          items: order.ordinazione || [],
          totale: gross,
          sconto: order.sconto || 0,
          delivery: order.delivery || null,
          deliveryFee: delFee,
          netto: Math.max(0, gross - delFee),
          digitale: order.digitale === true,
          cameriere: order.cameriere || ''
        });
      });

      this.ordiniAsporto.forEach(order => {
        const gross = (order.totale || 0) / 100;
        const delName = order.delivery || (order.isDelivery ? 'Delivery' : null);
        const delFee = this.calculateDeliveryFee(delName, gross);
        orders.push({
          id: order._id,
          type: 'asporto',
          identifier: `Asporto - ${order.nome || 'Cliente'}`,
          items: order.ordinazione || [],
          totale: gross,
          sconto: 0,
          delivery: delName,
          deliveryFee: delFee,
          netto: Math.max(0, gross - delFee),
          digitale: order.digitale !== undefined ? order.digitale === true : !!order.stripeSessionId,
          cameriere: ''
        });
      });

      return orders;
    },
    summaryStats() {
      let gross = 0;
      let deliveryFees = 0;
      this.unifiedOrders.forEach(o => {
        gross += o.totale;
        deliveryFees += o.deliveryFee;
      });
      return {
        gross,
        deliveryFees,
        net: Math.max(0, gross - deliveryFees)
      };
    }
  },
  methods: {
    getToken() {
      try {
        if (!localStorage.token) return localStorage.getItem('token') || '';
        const parsed = JSON.parse(localStorage.token);
        return typeof parsed === 'string' ? parsed : (localStorage.getItem('token') || '');
      } catch (e) {
        return localStorage.getItem('token') || '';
      }
    },
    home() {
      this.$router.push({ name: "Gestionale" });
    },
    calculateDeliveryFee(deliveryName, grossAmount) {
      if (!deliveryName || !this.deliveryPlatforms || !this.deliveryPlatforms.length) return 0;
      const platName = String(deliveryName).trim().toLowerCase();
      const plat = this.deliveryPlatforms.find(p => p.nome && p.nome.trim().toLowerCase() === platName);
      if (!plat) return 0;
      const perc = Number(plat.percentuale) || 0;
      const fixed = Number(plat.quotaFissa) || 0;
      const fee = (grossAmount * (perc / 100)) + fixed;
      return Math.min(grossAmount, fee);
    },
    async fetchData() {
      this.loading = true;
      const formattedDate = moment(this.date).format('YYYY-MM-DD');
      const token = this.getToken();
      const authHeader = `Bearer ${token}`;

      try {
        const response = await this.axios.post(this.store.path + 'riepilogo/getRiepilogo', {
          date: formattedDate,
          filter: this.filter,
          headers: {
            authorization: authHeader
          }
        }, {
          headers: {
            authorization: authHeader
          }
        });
        
        if (response.data) {
          this.items = response.data.items || [];
          this.ordiniTavolo = response.data.ordiniTavolo || [];
          this.ordiniAsporto = response.data.ordiniAsporto || [];
          this.deliveryPlatforms = response.data.deliveryPlatforms || [];
        }
      } catch (error) {
        console.error("Errore durante il caricamento del riepilogo:", error);
      } finally {
        this.loading = false;
      }
    },
    async updatePaymentMethod(orderId, orderType, isDigitale) {
      try {
        const token = this.getToken();
        const authHeader = `Bearer ${token}`;

        await this.axios.post(this.store.path + 'riepilogo/updateMetodoPagamento', {
          orderId,
          orderType,
          digitale: isDigitale,
          headers: {
            authorization: authHeader
          }
        }, {
          headers: {
            authorization: authHeader
          }
        });
      } catch (error) {
        console.error("Errore nell'aggiornamento del metodo di pagamento:", error);
      }
    },
    exportToExcel() {
      const formattedDate = moment(this.date).format('YYYY-MM-DD');
      let dataToExport = [];
      let filename = `Riepilogo_${formattedDate}.xlsx`;

      if (this.viewMode === 'piatti') {
        dataToExport = this.items.map(item => ({
          'Nome Piatto': item.piatto,
          'Portata': item.portata,
          'Quantità': item.quantita,
          'Guadagno (€)': item.guadagno ? item.guadagno.toFixed(2) : '0.00'
        }));
      } else {
        dataToExport = this.unifiedOrders.map(order => ({
          'Tipo': order.type === 'tavolo' ? 'Tavolo' : 'Asporto',
          'Identificativo': order.identifier,
          'Delivery': order.delivery || 'Nessuno',
          'Totale Lordo (€)': order.totale.toFixed(2),
          'Commissione Delivery (€)': order.deliveryFee.toFixed(2),
          'Netto Cassa (€)': order.netto.toFixed(2),
          'Metodo Pagamento': order.digitale ? 'Digitale' : 'Contanti'
        }));
      }

      const worksheet = XLSX.utils.json_to_sheet(dataToExport);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Riepilogo');
      XLSX.writeFile(workbook, filename);
    }
  },
  watch: {
    date() {
      this.fetchData();
    },
    filter() {
      this.fetchData();
    }
  },
  mounted() {
    this.fetchData();
  }
}
</script>

<style scoped>
.sci-view-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: var(--sci-bg, #f8fafc);
  color: var(--sci-text-main, #0f172a);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  padding-bottom: 60px;
}

/* Header */
.sci-header {
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 28px;
  background: rgba(255, 255, 255, 0.88);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--sci-border, #e2e8f0);
}

.sci-header-left {
  display: flex;
  align-items: center;
  gap: 18px;
}

.sci-btn-back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 16px;
  background: #ffffff;
  border: 1px solid var(--sci-border, #e2e8f0);
  border-radius: 12px;
  color: var(--sci-text-muted, #475569);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.22, 1, 0.36, 1);
}

.sci-btn-back:hover {
  background: #f1f5f9;
  color: var(--sci-text-main, #0f172a);
  border-color: #cbd5e1;
}

.sci-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
}

.sci-subtitle {
  margin: 2px 0 0 0;
  font-size: 0.82rem;
  color: var(--sci-text-muted, #475569);
}

.sci-header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.sci-btn-primary {
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  padding: 0 20px;
  background: var(--sci-primary, #4f46e5);
  color: #ffffff;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.sci-btn-primary:hover:not(:disabled) {
  background: #4338ca;
}

.sci-btn-secondary {
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  padding: 0 18px;
  background: #ffffff;
  border: 1px solid var(--sci-border, #e2e8f0);
  border-radius: 12px;
  color: var(--sci-text-main, #0f172a);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.sci-btn-secondary:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.sci-main-content {
  max-width: 1300px;
  margin: 0 auto;
  width: 100%;
  padding: 24px 20px;
}

/* Card */
.sci-card {
  background: #ffffff;
  border: 1px solid var(--sci-border, #e2e8f0);
  border-radius: 18px;
  box-shadow: 0 4px 6px -1px rgba(15, 23, 42, 0.05);
  padding: 22px;
}

/* KPI Grid */
.kpi-bento-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
}

.kpi-card {
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 20px;
}

.kpi-icon-box {
  width: 52px;
  height: 52px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.35rem;
  flex-shrink: 0;
}

.gross-icon { background: #eef2ff; color: #4f46e5; }
.delivery-fee-icon { background: #fff1f2; color: #e11d48; }
.net-icon { background: #ecfdf5; color: #059669; }

.kpi-net-card {
  border-color: #a7f3d0;
  background: linear-gradient(to right, #ffffff, #f0fdf4);
}

.kpi-info {
  display: flex;
  flex-direction: column;
}

.kpi-label {
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  color: var(--sci-text-muted, #475569);
  letter-spacing: 0.4px;
}

.kpi-number {
  font-size: 1.45rem;
  font-weight: 800;
  margin: 3px 0 2px 0;
}

.kpi-subtext {
  font-size: 0.78rem;
}

/* Toolbar Grid */
.toolbar-grid {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 24px;
  align-items: center;
}

@media (max-width: 992px) {
  .toolbar-grid {
    grid-template-columns: 1fr;
  }
}

.control-label {
  display: block;
  font-size: 0.82rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: var(--sci-text-muted, #475569);
  margin-bottom: 8px;
}

.segmented-control {
  display: inline-flex;
  background: #f1f5f9;
  padding: 4px;
  border-radius: 12px;
  gap: 4px;
}

.segment-btn {
  border: none;
  background: transparent;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--sci-text-muted, #475569);
  cursor: pointer;
  transition: all 0.2s;
}

.segment-btn:hover {
  color: var(--sci-text-main, #0f172a);
}

.segment-btn.active {
  background: #ffffff;
  color: var(--sci-primary, #4f46e5);
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
}

/* Table */
.sci-table {
  width: 100%;
  border-collapse: collapse;
}

.sci-table th {
  padding: 14px 16px;
  font-size: 0.82rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--sci-text-muted, #475569);
  border-bottom: 2px solid var(--sci-border, #e2e8f0);
}

.sci-table-row td {
  padding: 14px 16px;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: middle;
}

.item-name {
  font-weight: 700;
  color: var(--sci-text-main, #0f172a);
}

.badge-portata {
  padding: 4px 10px;
  border-radius: 20px;
  background: #f1f5f9;
  color: #475569;
  font-size: 0.8rem;
  font-weight: 600;
}

.badge-qty {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  background: #eef2ff;
  color: #4f46e5;
  font-weight: 700;
  font-size: 0.95rem;
}

.item-revenue {
  font-size: 1.05rem;
  font-weight: 700;
  color: #059669;
}

.order-id-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.order-type-badge {
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 700;
}

.type-table { background: #e0f2fe; color: #0369a1; }
.type-asporto { background: #fef3c7; color: #b45309; }

.order-delivery-badge {
  display: inline-flex;
  align-self: flex-start;
  padding: 2px 6px;
  border-radius: 4px;
  background: #eef2ff;
  color: #4f46e5;
  font-size: 0.72rem;
  font-weight: 700;
}

.order-identifier {
  font-weight: 700;
  font-size: 0.95rem;
}

.order-items-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.order-subitem {
  font-size: 0.88rem;
  line-height: 1.4;
}

.subitem-qty {
  font-weight: 700;
  color: #4f46e5;
  margin-right: 6px;
}

.order-net-val {
  font-size: 1.05rem;
  font-weight: 800;
  color: #059669;
}

.sci-select-sm {
  height: 36px;
  padding: 0 10px;
  border-radius: 8px;
  border: 1px solid var(--sci-border, #e2e8f0);
  background: #ffffff;
  color: var(--sci-text-main, #0f172a);
  font-size: 0.85rem;
  font-weight: 600;
  outline: none;
  cursor: pointer;
  transition: border-color 0.2s;
}

.sci-select-sm:focus {
  border-color: var(--sci-primary, #4f46e5);
}

.empty-icon-halo {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #f1f5f9;
  color: #94a3b8;
  font-size: 1.6rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 14px auto;
}

.empty-title {
  font-size: 1.15rem;
  font-weight: 700;
  margin-bottom: 4px;
}

.empty-desc {
  font-size: 0.85rem;
  color: var(--sci-text-muted, #475569);
}
</style>
